<template>
  <transition
    enter-active-class="transition duration-500 ease-out"
    enter-from-class="opacity-0 translate-y-1"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-500 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-1"
  >
    <div
      v-if="visible"
      class="pointer-events-none absolute bottom-3 left-3 z-[10000] flex max-w-[min(18rem,calc(100vw-1.5rem))] items-center gap-2 rounded-lg border border-white/15 bg-zinc-950/92 px-2.5 py-2 shadow-lg backdrop-blur-md"
      aria-live="polite"
      aria-label="Campaign update received"
    >
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-cyan-400/35 bg-cyan-500/15 text-cyan-300"
      >
        <Clapperboard class="h-5 w-5" :stroke-width="2.25" aria-hidden="true" />
      </span>
      <div class="min-w-0 flex-1 text-left leading-tight">
        <p class="text-[11px] font-semibold tracking-wide text-white">
          Campaign updated
        </p>
        <p class="mt-0.5 text-[10px] font-medium text-white/70">
          New schedule from the server
        </p>
      </div>
    </div>
  </transition>
</template>

<script lang="ts" setup>
import { Clapperboard } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import usePlayer from '@/composables/usePlayer'
import { campaignUpdateNotificationVisible } from '@/helpers/campaignUpdateNotificationVisible'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'
import { isRegistrationScheduleAheadOfOnAir } from '@/helpers/registrationAheadOfPlayback'

const { state } = usePlayer()

const VISIBLE_DURATION_MS = 2500
const TICK_MS = 200

const nowMs = ref(Date.now())
let tickId: ReturnType<typeof setInterval> | null = null

const visible = computed(() =>
  campaignUpdateNotificationVisible(
    state.tenant,
    state.campaignUpdateReceivedAt,
    nowMs.value,
    VISIBLE_DURATION_MS,
    {
      suppressForStatusOverlayFeedback:
        state.showDebugModal
        && (
          state.registrationStatusRefreshPending
          || state.pendingPlaylistSwap != null
          || isRegistrationScheduleAheadOfOnAir({
            roots: state.registrationScheduleRoots,
            assignedMasterPlaylistUrn: state.assignedMasterPlaylistUrn,
            campaign: state.campaign,
            now: getLocalAsUtcInstant()
          })
        )
    }
  )
)

onMounted(() => {
  tickId = setInterval(() => {
    nowMs.value = Date.now()
  }, TICK_MS)
})

onBeforeUnmount(() => {
  if (tickId !== null) {
    clearInterval(tickId)
    tickId = null
  }
})
</script>
